package com.essntl.features.itinerary.ui.all_itinerary.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextOverflow
import com.essntl.features.client.ui.ClientDialogButton
import com.essntl.features.itinerary.domain.model.ItineraryModel
import com.essntl.features.itinerary.domain.model.shareLink
import com.essntl.features.itinerary.ui.all_itinerary.AllItineraryNavigation
import com.essntl.features.itinerary.ui.all_itinerary.AllItineraryState
import com.essntl.features.itinerary.ui.combinedAreas
import com.essntl.features.profile.ui.OwnerItem
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.iconbutton.primary.EsSmallPrimaryIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.tooltip.EsTooltip
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.EsRowDefaultItem
import com.essntl.uikit.components.utils.EsRowItem
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberContains
import com.essntl.uikit.rememberNumberOfDaysPrettyString
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.dateformat.ui.formatDate
import core.dateformat.ui.formatDateRange

@Composable
fun ItineraryItem(
    state: AllItineraryState,
    onNavigation: (AllItineraryNavigation) -> Unit,
    index: Int,
    itineraryModel: ItineraryModel,
    isSelected: Boolean,
    modifier: Modifier = Modifier,
) {

    val areas = rememberContains(
        state.getAllArea.data.orEmpty(),
        itineraryModel.proposal?.areas.orEmpty().toSet(),
    ) { area -> area.id }

    val interactionSource = remember { MutableInteractionSource() }

    val isHovered by interactionSource.collectIsHoveredAsState()

    val isPressed by interactionSource.collectIsPressedAsState()

    Column(
        modifier =
            modifier
                .clip(RoundedCornerShape(EsConstants.RoundedCornerSize)),
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier =
                Modifier
                    .background(MaterialTheme.esColorScheme.bgSecondary)
                    .background(
                        when {
                            isPressed || isSelected ->
                                MaterialTheme.esColorScheme.tintedPressed

                            isHovered ->
                                MaterialTheme.esColorScheme.tintedHover

                            index % 2 == 0 ->
                                MaterialTheme.esColorScheme.bgSecondaryHover.copy(alpha = 0.7f)

                            else ->
                                Color.Transparent
                        },
                    )
                    .padding(
                        vertical = EsConstants.ListRowVerticalPadding,
                        horizontal = EsConstants.ListRowHorizontalPadding,
                    )
                    .height(EsConstants.ListRowItemHeight)
                    .pointerHoverIcon(PointerIcon.Hand)
                    .focusProperties { canFocus = false }
                    .clickable(
                        interactionSource = interactionSource,
                        indication = null,
                        onClick = {
                            onNavigation(
                                AllItineraryNavigation.Overview(
                                    itineraryId = itineraryModel.id,
                                ),
                            )
                        },
                    ),
        ) {
            // Itinerary title
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier =
                    Modifier
                        .weight(2f)
                        .fillMaxHeight(),
            ) {
                Box(
                    contentAlignment = Alignment.Center,
                    modifier =
                        Modifier
                            .weight(2f)
                            .fillMaxSize(),
                ) {
                    Text(
                        text = itineraryModel.proposal?.title ?: "itinerary Title",
                        color = MaterialTheme.esColorScheme.textPrimary,
                        style = MaterialTheme.esTypography.s2SemiBold16,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier =
                            Modifier
                                .align(Alignment.CenterStart),
                    )
                }
            }

            EsRowItem {
                OwnerItem(
                    ownerId = itineraryModel.proposal?.owner?.id,
                    getAllProfile = state.getAllProfile,
                    isUpdateOwnerLoading = false,
                    enabled = false,
                    onOwnerChange = { },
                    modifier =
                        Modifier
                            .align(Alignment.CenterStart),
                )
            }

            // Destination
            EsRowDefaultItem(
                text = areas.combinedAreas,
            )

            val dateDiff = rememberNumberOfDaysPrettyString(
                itineraryModel.proposal?.fromDate,
                itineraryModel.proposal?.toDate,
            )

            // Date
            EsRowDefaultItem(
                text =
                    listOf(
                        itineraryModel.proposal?.fromDate,
                        itineraryModel.proposal?.toDate,
                    ).formatDateRange(),
                extraContent = {
                    Text(
                        text = dateDiff,
                        color = MaterialTheme.esColorScheme.textPrimary,
                        style = MaterialTheme.esTypography.p2Medium14,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                },
            )

            // Client
            EsRowDefaultItem(
                text = "",
                extraContent = {
                    ClientDialogButton(
                        client = itineraryModel.proposal?.primaryClient,
                        onClick = {
                            onNavigation(
                                AllItineraryNavigation.EditClient(
                                    client = it,
                                    itinerary = itineraryModel,
                                ),
                            )
                        },
                    )
                },
            )

            // Date confirmed
            EsRowDefaultItem(
                text = itineraryModel.createdAt.formatDate("."),
            )

            // Last changed
            EsRowDefaultItem(
                text =
                    itineraryModel.proposal?.createdAt?.formatDate(".").orEmpty(),
            )

            // Actions
            EsRowItem {
                Row(
                    horizontalArrangement = Arrangement.Start,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    val uriHandler = LocalUriHandler.current

                    EsTooltip(
                        text = "Open in web browser",
                    ) {
                        EsSmallOutlineIconButton(
                            onClick = {
                                itineraryModel.shareLink?.let { link ->
                                    uriHandler.openUri(
                                        uri = link,
                                    )
                                }
                            },
                            iconRes = EsIcons.Browse,
                            modifier = Modifier
                                .focusProperties { canFocus = false },
                        )
                    }

                    Spacer(modifier = Modifier.width(1.5f.spacing))

                    EsTooltip(
                        text = "Edit",
                    ) {
                        EsSmallPrimaryIconButton(
                            onClick = {
                                onNavigation(
                                    AllItineraryNavigation.EditItinerary(
                                        itineraryId = itineraryModel.id,
                                    ),
                                )
                            },
                            iconRes = EsIcons.Edit,
                            modifier =
                                Modifier
                                    .focusProperties { canFocus = false },
                        )
                    }
                }
            }
        }
    }
}
